import { getAppVersion, isBrowser, toQueryString } from '@croquiscom/web2app';

import { ApiErrorCodes, ApiErrors } from '@common/api-request';
import { Router } from '@common/checkout-utils';
import { Dialog, LoadingManager, Toast } from '@common/overlay-manager';
import { loginKakao } from '@common/utils';

import { createOrderSheet } from '../graphql/order-sheet.2';
import { CreateOrderSheetOrderItemInput } from '../graphql/order-sheet_types.2';
import { handleNetworkError } from './handle_network_error';
import { redirectInquiries } from './redirectInquiries';

export function forceRefreshPage() {
  window.location.reload();
}

export interface CreateOrderSheetArgs {
  order_item_list: CreateOrderSheetOrderItemInput[];
  selected_user_account_coupon_id_list?: string[] | null;
  from: 'cart' | 'product';
  catalog_product_id?: string;
}

export async function createOrderSheetByOrderItemList({
  order_item_list,
  selected_user_account_coupon_id_list = null,
  from,
}: CreateOrderSheetArgs) {
  try {
    LoadingManager.open({ isMiniLoader: true });

    const result = await createOrderSheet(
      { input: { order_item_list, selected_user_account_coupon_id_list } },
      {
        show_alert: false,
      },
    );
    if (result.errors) {
      if (result.errors![0].message !== ApiErrors.NOT_LOGGED_IN) {
        Toast.show('주문 중에 에러가 발생했습니다');
      }
      return;
    }
    if (isBrowser()) {
      Router.push(
        { pathname: `/order-sheets/${result.data.createOrderSheet}`, query: { from } },
        from === 'product' ? { action: 'createOrderSheet' } : undefined,
      );
    }
  } catch (error: any) {
    if (
      error.extensions?.code === ApiErrorCodes.INSUFFICIENT_PRODUCT_QUANTITY ||
      error.extensions?.code === ApiErrorCodes.ORDER_ITEM_PRODUCT_ITEM_SOLDOUT
    ) {
      Dialog.alert(error?.description);
      if (from === 'cart') {
        forceRefreshPage();
      }
      return;
    }
    if (
      await handleNetworkError(error, async () =>
        createOrderSheetByOrderItemList({ order_item_list, selected_user_account_coupon_id_list, from }),
      )
    ) {
      return;
    }
    Dialog.alert(error?.description || '알 수 없는 오류가 발생했습니다. 고객센터로 문의해주세요');
    Toast.show('주문 중에 에러가 발생했습니다');
  } finally {
    LoadingManager.close();
  }
}

interface CreateOrderSheetOptions {
  show_alert?: boolean;
  ignore_login_error?: boolean;
  onNotLoggedIn?: () => void;
}

const handleCreateOrderSheet = async (
  createOrderSheetArgs: CreateOrderSheetArgs,
  options?: CreateOrderSheetOptions,
) => {
  const {
    order_item_list,
    selected_user_account_coupon_id_list = null,
    from,
    catalog_product_id,
  } = createOrderSheetArgs;

  try {
    const isInApp = Boolean(getAppVersion());
    const appAction = window.location.pathname.includes('select-option') ? 'createOrderSheet' : undefined;

    LoadingManager.open({ isMiniLoader: true });

    const result = await createOrderSheet(
      { input: { order_item_list, selected_user_account_coupon_id_list } },
      {
        show_alert: options?.show_alert ?? false,
        ignore_error: options?.ignore_login_error ? ApiErrors.NOT_LOGGED_IN : undefined,
      },
    );

    if (result.errors) {
      const message = result.errors?.[0].message;
      const notLoggedIn =
        message === ApiErrors.NOT_LOGGED_IN || result.errors?.[0].extensions?.code === 'not_logged_in';

      if (notLoggedIn) {
        sessionStorage.setItem(`${catalog_product_id}?purchased-items`, JSON.stringify(createOrderSheetArgs));
        options?.onNotLoggedIn?.();
      } else {
        Toast.show('주문 중에 에러가 발생했습니다');
      }
      return;
    }

    if (isBrowser()) {
      if (isInApp) {
        Router.open(`/order-sheets/${result.data.createOrderSheet}?${toQueryString({ from })}`, {
          title: '결제',
          type: 'checkout',
          action: appAction,
        });
      } else {
        Router.push(`/order-sheets/${result.data.createOrderSheet}?${toQueryString({ from })}`, {
          title: '결제',
          type: 'checkout',
        });
      }
    }
  } catch (error: any) {
    const retried = await handleNetworkError(error, async () =>
      createOrderSheetByOrderItemList({ order_item_list, selected_user_account_coupon_id_list, from }),
    );
    if (retried) {
      return;
    }

    await Dialog.alert(error?.description || '알 수 없는 오류가 발생했습니다. 고객센터로 문의해주세요');
    Toast.show('주문 중에 에러가 발생했습니다');

    if (error.extensions?.code === 'seller_blocked_user_validation_failed' && catalog_product_id) {
      redirectInquiries(catalog_product_id);
    }
  } finally {
    LoadingManager.close();
  }
};

export const createOrderSheetInPDP = async (args: CreateOrderSheetArgs) => {
  return handleCreateOrderSheet(args);
};

export const createOrderSheetInPDPForKakaoPurchase = async (args: CreateOrderSheetArgs) => {
  return handleCreateOrderSheet(args, {
    ignore_login_error: true,
    onNotLoggedIn: () => loginKakao(window.location.href),
  });
};
